import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Facebook, Twitter, Instagram, Linkedin, Mail } from 'lucide-react';
import axios from 'axios';
import { useToast } from '@/hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Footer = () => {
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast({
        title: "Subscribed!",
        description: "You'll receive the latest deals in your inbox"
      });
      setEmail('');
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to subscribe",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-gray-900 text-gray-300" data-testid="footer">
      {/* Newsletter Section */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center text-white">
            <Mail className="w-12 h-12 mx-auto mb-4" />
            <h2 className="text-3xl font-bold mb-2" data-testid="newsletter-heading">
              Never Miss a Deal!
            </h2>
            <p className="mb-6 text-emerald-50" data-testid="newsletter-description">
              Subscribe to get exclusive deals and savings directly in your inbox
            </p>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 bg-white text-gray-900"
                data-testid="newsletter-email-input"
              />
              <Button
                type="submit"
                disabled={loading}
                className="bg-gray-900 hover:bg-gray-800 text-white"
                data-testid="newsletter-subscribe-button"
              >
                {loading ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* About */}
          <div>
            <h3 className="text-xl font-bold text-white mb-4" data-testid="footer-about-heading">
              Yalla ! Lets Save.
            </h3>
            <p className="text-sm mb-4">
              UAE's leading platform for discovering the best deals, discounts, and offers across all emirates.
            </p>
            <div className="flex space-x-3">
              <a href="#" className="hover:text-emerald-400 transition-colors" aria-label="Facebook">
                <Facebook size={20} />
              </a>
              <a href="#" className="hover:text-emerald-400 transition-colors" aria-label="Twitter">
                <Twitter size={20} />
              </a>
              <a href="#" className="hover:text-emerald-400 transition-colors" aria-label="Instagram">
                <Instagram size={20} />
              </a>
              <a href="#" className="hover:text-emerald-400 transition-colors" aria-label="LinkedIn">
                <Linkedin size={20} />
              </a>
            </div>
          </div>

          {/* Categories */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Categories</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/deals?category=dining" className="hover:text-emerald-400 transition-colors">Dining</Link></li>
              <li><Link to="/deals?category=shopping" className="hover:text-emerald-400 transition-colors">Shopping</Link></li>
              <li><Link to="/deals?category=travel" className="hover:text-emerald-400 transition-colors">Travel</Link></li>
              <li><Link to="/deals?category=entertainment" className="hover:text-emerald-400 transition-colors">Entertainment</Link></li>
              <li><Link to="/deals?category=beauty" className="hover:text-emerald-400 transition-colors">Beauty</Link></li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Support</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/faq" className="hover:text-emerald-400 transition-colors">FAQ</Link></li>
              <li><Link to="/contact" className="hover:text-emerald-400 transition-colors">Contact Us</Link></li>
              <li><Link to="/how-it-works" className="hover:text-emerald-400 transition-colors">How It Works</Link></li>
              <li><Link to="/terms" className="hover:text-emerald-400 transition-colors">Terms of Service</Link></li>
              <li><Link to="/privacy" className="hover:text-emerald-400 transition-colors">Privacy Policy</Link></li>
            </ul>
          </div>

          {/* Locations */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">UAE Locations</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/deals?location=Dubai" className="hover:text-emerald-400 transition-colors">Dubai</Link></li>
              <li><Link to="/deals?location=Abu Dhabi" className="hover:text-emerald-400 transition-colors">Abu Dhabi</Link></li>
              <li><Link to="/deals?location=Sharjah" className="hover:text-emerald-400 transition-colors">Sharjah</Link></li>
              <li><Link to="/deals?location=Ajman" className="hover:text-emerald-400 transition-colors">Ajman</Link></li>
              <li><Link to="/deals?location=Ras Al Khaimah" className="hover:text-emerald-400 transition-colors">Ras Al Khaimah</Link></li>
            </ul>
          </div>
        </div>

        <Separator className="my-8 bg-gray-700" />

        <div className="flex flex-col md:flex-row justify-between items-center text-sm">
          <p data-testid="copyright">&copy; 2025 Yalla ! Lets Save. All rights reserved.</p>
          <p className="mt-2 md:mt-0">Made with ❤️ in the UAE</p>
        </div>
      </div>
    </footer>
  );
};
